
import { useState } from "react";
import { Shop, Form, FormField } from "@/components/dashboard/shops/types";
import { createForm } from "@/lib/supabase/forms";
import { useOrganizationId } from "@/components/dashboard/products/hooks/useOrganizationId";
import { toast } from "sonner";

export const useFormCreation = (
  shop: Shop,
  refreshForms: () => Promise<Form[]>,
  setCurrentForm: (form: Form) => void,
  createFormTemplate: () => FormField[],
  setFormFields: (fields: FormField[]) => void
) => {
  const [isSaving, setIsSaving] = useState(false);
  const { organizationId } = useOrganizationId();

  // Create a new form
  const handleCreateForm = async () => {
    if (!organizationId) {
      console.error("Organisation ID manquant pour créer un formulaire");
      toast.error("Erreur", {
        description: "ID d'organisation manquant pour créer un formulaire"
      });
      return false;
    }
    
    if (!shop.id) {
      console.error("Shop ID manquant pour créer un formulaire");
      toast.error("Erreur", {
        description: "ID de boutique manquant pour créer un formulaire"
      });
      return false;
    }
    
    // Ensure shop.id is a valid UUID
    if (typeof shop.id !== 'string' || !shop.id.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)) {
      console.error(`ID de shop invalide: ${shop.id}. Un UUID valide est requis.`);
      toast.error("Erreur", {
        description: "L'ID de la boutique n'est pas un UUID valide"
      });
      return false;
    }
    
    console.log(`Création de formulaire avec shop_id valide: ${shop.id}`);
    
    const defaultFields = createFormTemplate();
    
    const newForm: Omit<Form, 'id' | 'created_at' | 'updated_at'> = {
      name: "Formulaire de contact",
      organization_id: organizationId,
      shop_id: shop.id,
      cta_button_text: "Acheter maintenant",
      fields: defaultFields,
      product_ids: [],
      // Initialize new fields with empty values
      phone: "",
      customer_name: "",
      email: "",
      country: "",
      region: "",
      municipality: "",
      address: "",
      external_id: `FORM-${Date.now()}`
    };
    
    try {
      setIsSaving(true);
      console.log("Création d'un nouveau formulaire avec les données:", newForm);
      
      const { success, data, error } = await createForm(newForm);
      
      if (success && data) {
        console.log("Formulaire créé avec succès:", data);
        await refreshForms(); // Refresh forms after creation
        setCurrentForm(data);
        setFormFields(data.fields);
        return true;
      } else {
        console.error("Erreur lors de la création du formulaire:", error);
        toast.error("Erreur", {
          description: error?.message || "Échec de la création du formulaire"
        });
        return false;
      }
    } catch (error) {
      console.error("Exception lors de la création du formulaire:", error);
      toast.error("Erreur", {
        description: "Une erreur s'est produite lors de la création du formulaire"
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return { handleCreateForm };
};
